import moment from 'moment-timezone';
import type { OperatingHoursCalc } from '@pperform/pp-data-mappers';

const getAvailableServices = (operatingHours: OperatingHoursCalc[]) => {
  const serviceNames = new Set();

  operatingHours.forEach((item) => {
    serviceNames.add(item.serviceName);
  });

  return Array.from(serviceNames);
};

export const getOpenStatus = (
  operatingHoursCalc: OperatingHoursCalc[],
): '' | 'OPEN_NOW' | 'CLOSED' => {
  // filter out operatingHours that don't have firstOpenHours and secondOpenHours
  const operatingHours = operatingHoursCalc.filter(
    (item) => item.firstOpenHours || item.secondOpenHours,
  );

  // No operating hours, closed
  if (!operatingHours.length) {
    return 'CLOSED';
  }

  // if collection point knowns multiple services, hide the open status
  // https://parcelperform.slack.com/archives/C060VPYQ98C/p1698736400179359
  if (getAvailableServices(operatingHours).length > 1) {
    return '';
  }

  const timezone = operatingHours[0].timeZone;
  const now = moment().tz(timezone);

  // Find the operating hours for the current day
  const todayOpeningHours = operatingHours.find(
    (item) => item.operatingDay === now.format('dddd'),
  );

  // No opening hours found for the current day, closed
  if (!todayOpeningHours) {
    return 'CLOSED';
  }

  // Parse the open hours for the current day
  // Assume the open hours are in the format of 'hh:mm-hh:mm'
  const firstOpenRange = todayOpeningHours.firstOpenHours?.split('-');
  const secondOpenRange = todayOpeningHours.secondOpenHours?.split('-');

  // No opening hours found for the current day, closed
  if (
    (!firstOpenRange || firstOpenRange.length !== 2) &&
    (!secondOpenRange || secondOpenRange.length !== 2)
  ) {
    return 'CLOSED';
  }

  const [firstOpenTime, firstCloseTime] = (firstOpenRange || []).map((time) =>
    moment.tz(time, 'HH:mm', timezone),
  );

  const [secondOpenTime, secondCloseTime] = (secondOpenRange || []).map(
    (time) => moment.tz(time, 'HH:mm', timezone),
  );

  // Check if the current time is within the opening hours
  if (
    (firstOpenTime &&
      firstCloseTime &&
      now.isBetween(firstOpenTime, firstCloseTime, null, '[]')) ||
    (secondOpenTime &&
      secondCloseTime &&
      now.isBetween(secondOpenTime, secondCloseTime, null, '[]'))
  ) {
    return 'OPEN_NOW';
  }

  return 'CLOSED';
};
